// Registry Control Plane — HTTP client.
//
// Copies the apiFetch pattern from DivergenceDashboard/MigrationQueue.js
// (getUser() -> Bearer token -> REACT_APP_INTEGRATION_MICROSERVICE_URL) but is
// self-contained: we do NOT import from the superseded DivergenceDashboard.
//
// Base URL = `${INTEGRATION_URL}/registry` per the backend contract.

import { getUser } from '../../../auth/authService';

const INTEGRATION_URL = process.env.REACT_APP_INTEGRATION_MICROSERVICE_URL;
const API_BASE = `${INTEGRATION_URL}/registry`;

async function apiFetch(path, options = {}) {
    const user = await getUser();
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.access_token}`,
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const text = await res.text();
        let detail;
        try { detail = JSON.parse(text).detail; } catch { detail = text; }
        throw new Error(detail || `HTTP ${res.status}`);
    }
    return res.json();
}

const postJson = (path, body) =>
    apiFetch(path, { method: 'POST', body: JSON.stringify(body) });

// GET /registry/descriptors -> { registered_ops, descriptors }
export const getDescriptors = () => apiFetch('/descriptors');

// GET /registry/clients -> { clients: [{client_id, title}] }, active only, alphabetized
export const getActiveClients = () => apiFetch('/clients');

// GET /registry/manifest -> { ops: { <op>: {status, evidence, tests} }, ... }
export const getManifest = () => apiFetch('/manifest');

// POST /registry/changeset/dry-run -> { changeset_id, ops, combined_blast_radius }
export const dryRun = (changeset) => postJson('/changeset/dry-run', changeset);

// POST /registry/changeset/apply -> { changeset_id, applied, status }
export const apply = (changeset) => postJson('/changeset/apply', changeset);

// POST /registry/changeset/{id}/rollback -> { changeset_id, reverted, status }
export const rollback = (id) =>
    apiFetch(`/changeset/${id}/rollback`, { method: 'POST' });

// GET /registry/changeset/{id} -> audit record
export const getAudit = (id) => apiFetch(`/changeset/${id}`);

// GET /registry/fk-graph/{clientId} -> { client_id, edges: [...] }
// Every FK link already set for a client -- pure metadata, fast regardless of
// account size. Independent of running a sweep (browse anytime).
export const getFkGraph = (clientId) => apiFetch(`/fk-graph/${clientId}`);

// POST /registry/account-fk-sweep/{clientId} -- streamed as newline-delimited
// JSON (one {"type":"progress","message"} line per step) so a multi-minute
// sweep isn't a silent spinner; the last line is
// {"type":"result","data": {client_id, applied, queued_for_review,
// needs_hub_pick, profile_errors, changeset_id, changeset_status}}.
// `onProgress(message)` fires for every progress line as it arrives.
export async function runAccountFkSweep(clientId, { apply = false, hubOverrides = [], onProgress } = {}) {
    const user = await getUser();
    const res = await fetch(`${API_BASE}/account-fk-sweep/${clientId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.access_token}` },
        body: JSON.stringify({ apply, hub_overrides: hubOverrides }),
    });
    if (!res.ok) {
        const text = await res.text();
        let detail;
        try { detail = JSON.parse(text).detail; } catch { detail = text; }
        throw new Error(detail || `HTTP ${res.status}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;
    let streamError = null;

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // last entry may be a partial line -- keep it for next chunk
        for (const line of lines) {
            if (!line.trim()) continue;
            const event = JSON.parse(line);
            if (event.type === 'progress') onProgress?.(event.message);
            else if (event.type === 'result') result = event.data;
            else if (event.type === 'error') streamError = event.message;
        }
    }
    if (streamError) throw new Error(streamError);
    if (result === null) throw new Error('Sweep stream ended without a result');
    return result;
}
